import type { Entity, MutationKind } from '../game/components'
import { MUTATION_DEFS } from '../game/mutations'
import { ALL_POWER_IDS, POWER_DEFS, powerLevel, type PowerId } from '../game/powers'
import { skillIconSvg } from './skill-icons'

export interface MutationTray {
  tick(player: Entity): void
}

interface ChipRefs {
  root: HTMLElement
  count: HTMLElement
  tooltip: HTMLElement
}

function tintToRgba([r, g, b]: [number, number, number], alpha: number): string {
  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`
}

// Mutation glyphs (kept as unicode per Charles — powers moved to SVG).
// Shared with the growth-spurt cards so chip and card read as the same thing.
export const MUTATION_GLYPH: Record<MutationKind, string> = {
  'bouquet': '✿',
  'static-cling': '⚡',
  'nucleus-envy': '◎',
  'wet-socks': '~',
  'borrowed-teeth': 'T',
  'eyes-everywhere': '👁',
}

export function createMutationTray(): MutationTray {
  const root = document.getElementById('mutation-tray')
  if (!root) {
    return { tick: () => {} }
  }
  const chips = new Map<MutationKind, ChipRefs>()
  const powerChips = new Map<PowerId, ChipRefs>()
  let lastPulseKind: MutationKind | null = null

  function makePowerChip(id: PowerId): ChipRefs {
    const def = POWER_DEFS[id]
    const chip = document.createElement('div')
    chip.className = 'mutation-chip'
    chip.dataset.sign = 'power'
    chip.style.background = tintToRgba(def.tint, 0.28)

    const glyph = document.createElement('span')
    glyph.className = 'mutation-chip-glyph'
    glyph.innerHTML = skillIconSvg(id)
    chip.appendChild(glyph)

    const count = document.createElement('span')
    count.className = 'mutation-chip-count'
    count.textContent = '1'
    chip.appendChild(count)

    const tooltip = document.createElement('span')
    tooltip.className = 'mutation-chip-tooltip'
    tooltip.textContent = `${def.name} — ${def.description}`
    chip.appendChild(tooltip)

    root!.appendChild(chip)
    return { root: chip, count, tooltip }
  }

  function makeChip(kind: MutationKind): ChipRefs {
    const def = MUTATION_DEFS[kind]
    const chip = document.createElement('div')
    chip.className = 'mutation-chip'
    chip.dataset.sign = def.sign
    chip.style.background = tintToRgba(def.tint, 0.28)

    const glyph = document.createElement('span')
    glyph.className = 'mutation-chip-glyph'
    glyph.textContent = MUTATION_GLYPH[kind]
    chip.appendChild(glyph)

    const count = document.createElement('span')
    count.className = 'mutation-chip-count'
    count.textContent = '1'
    chip.appendChild(count)

    const tooltip = document.createElement('span')
    tooltip.className = 'mutation-chip-tooltip'
    tooltip.textContent = `${def.name} — ${def.flavor}`
    chip.appendChild(tooltip)

    root!.appendChild(chip)
    return { root: chip, count, tooltip }
  }

  return {
    tick(player) {
      const stacks = player.mutations?.stacks ?? {}
      const recent = player.mutations?.recentPickup

      // Add or update chips.
      for (const kind of Object.keys(stacks) as MutationKind[]) {
        const n = stacks[kind] ?? 0
        if (n <= 0) continue
        let chip = chips.get(kind)
        if (!chip) {
          chip = makeChip(kind)
          chips.set(kind, chip)
        }
        chip.count.textContent = String(n)
        // Refresh tooltip in case flavor copy changed (e.g. via voice handoff).
        const def = MUTATION_DEFS[kind]
        chip.tooltip.textContent = `${def.name} — ${def.flavor}`
      }

      // Remove chips for kinds no longer present (e.g. after death/respawn).
      for (const [kind, chip] of chips) {
        const n = stacks[kind] ?? 0
        if (n <= 0) {
          chip.root.remove()
          chips.delete(kind)
        }
      }

      // Power chips (amoeba-powers.md) — level shown as the count badge.
      for (const id of ALL_POWER_IDS) {
        const level = powerLevel(player, id)
        let chip = powerChips.get(id)
        if (level <= 0) {
          if (chip) {
            chip.root.remove()
            powerChips.delete(id)
          }
          continue
        }
        if (!chip) {
          chip = makePowerChip(id)
          powerChips.set(id, chip)
        }
        chip.count.textContent = `L${level}`
      }

      // Pulse animation: only triggers on the leading edge of a new pickup.
      if (recent && recent.kind !== lastPulseKind) {
        const chip = chips.get(recent.kind)
        if (chip) {
          chip.root.classList.remove('pulse')
          // Force reflow so the animation can restart.
          void chip.root.offsetWidth
          chip.root.classList.add('pulse')
        }
        lastPulseKind = recent.kind
      }
      if (!recent) {
        lastPulseKind = null
      }
    },
  }
}
